// back-controller.tsx
import React, {
    createContext,
    useCallback,
    useContext,
    useRef,
    PropsWithChildren,
} from "react";

type BackHandler = () => boolean; // return true if handled internally

type BackController = {
    push: (fn: BackHandler) => () => void; // register handler, returns unregister
    invoke: () => boolean;                 // invoke top-most handler
};

const Ctx = createContext<BackController | null>(null);

// specify the generic argument explicitly:
export const BackControllerProvider: React.FC<PropsWithChildren<{}>> = ({
    children,
}) => {
    const stackRef = useRef<BackHandler[]>([]);

    const push = useCallback((fn: BackHandler) => {
        stackRef.current.push(fn);
        return () => {
            const i = stackRef.current.lastIndexOf(fn);
            if (i >= 0) stackRef.current.splice(i, 1);
        };
    }, []);

    const invoke = useCallback(() => {
        const stack = stackRef.current;
        for (let i = stack.length - 1; i >= 0; i--) {
            try {
                if (stack[i]()) return true;
            } catch {
                // ignore errors and continue
            }
        }
        return false;
    }, []);

    return <Ctx.Provider value={{ push, invoke }}>{children}</Ctx.Provider>;
};

export const useBackController = () => {
    const ctx = useContext(Ctx);
    if (!ctx)
        throw new Error("useBackController must be used inside BackControllerProvider");
    return ctx;
};
